import React from 'react';
import parse from 'html-react-parser';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import HeartIcon from './HeartIcon.jsx';

import { eventPropType } from '../propTypes';
import { addEvent, removeEvent } from '../redux/savedEvents.slice.js';

function Event({
  currentDateTime, currentTimezone, date, description, endTime, hideEndTime, toggleTbcOn, id, link, listingImage, listingImageAlt, locateOnMapDescription, locationMappedinId, sponsorLink, sponsorLogo, sponsorNames, startTime, title, typeDescription, // eslint-disable-line max-len
}) {
  const dispatch = useDispatch();
  const savedEvents = useSelector((state) => state.savedEvents);

  const getDateTimeObject = (dateString) => DateTime.fromFormat(dateString, 'yyyy-MM-dd H:mm', { zone: 'Australia/Melbourne' }).setZone(currentTimezone);

  const renderListingImage = () => (
    <img
      alt={listingImageAlt}
      className="c-event-schedule__listing-image"
      src={listingImage}
    />
  );

  const isSaved = savedEvents.includes(id);

  const saveIconClickHandler = () => {
    if (isSaved) {
      dispatch(removeEvent(id));
    } else {
      dispatch(addEvent(id));
    }
  };

  const endTimeIsOnNextDay = parseInt(startTime.replace(':', ''), 10) > parseInt(endTime.replace(':', ''), 10);
  const startDateTime = getDateTimeObject(`${date} ${startTime}`);
  const endDateTime = getDateTimeObject(`${date} ${endTime}`).plus({ days: endTimeIsOnNextDay ? 1 : 0 });

  const status = currentDateTime >= endDateTime
    ? 'ended'
    : currentDateTime < endDateTime && currentDateTime >= startDateTime
      ? 'in-progress'
      : 'not-started';

  const descriptionWithLink = `<div>${description || ''} ${link ? `<a href="${link}">More</a>.` : ''}</div>`;

  const renderEventStatusText = () => {
    if (status === 'in-progress') {
      return (
        <span className="c-event-schedule__event-status c-event-schedule__event-status--in-progress">
          In Progress
        </span>
      );
    }

    if (status === 'ended') {
      return (
        <span className="c-event-schedule__event-status c-event-schedule__event-status--ended">
          Finished
        </span>
      );
    }

    return null;
  };

  return (
    <div className={`
      c-event-schedule__event
      ${status === 'ended' ? 'c-event-schedule__event--ended' : ''}
      ${status === 'in-progress' ? 'c-event-schedule__event--in-progress' : ''}
    `}
    >
      <div className="c-event-schedule__event-time">
        {toggleTbcOn ? (
          <span className="c-event-schedule__event-time-start">TBC</span>
        ) : (
          <span className="c-event-schedule__event-time-start">{startDateTime.toFormat('h:mm a')}</span>
        )}

        {!toggleTbcOn && !hideEndTime && (
          <span className="c-event-schedule__event-time-end">
&nbsp;to
            {endDateTime.toFormat('h:mm a')}
          </span>
        )}
      </div>

      <div className="c-event-schedule__event-card">
        <div className="c-event-schedule__event-header">
          <div className="c-event-schedule__event-header-left">
            {sponsorLink && sponsorLogo && (
              <a href={sponsorLink} target="_blank" rel="noreferrer">
                <img
                  alt={`sponsored by ${sponsorNames || 'name not provided'}`}
                  className="c-event-schedule__sponsor-logo"
                  src={sponsorLogo}
                />
              </a>
            )}

            {!sponsorLink && sponsorLogo && (
              <img
                alt={`sponsored by ${sponsorNames || 'name not provided'}`}
                className="c-event-schedule__sponsor-logo"
                src={sponsorLogo}
              />
            )}

            <div className="c-event-schedule__event-header-text">
              <h4 className="c-event-schedule__event-title">{title}</h4>

              {renderEventStatusText()}

              <span className="c-event-schedule__event-type-description">{typeDescription}</span>
            </div>
          </div>

          <div className="c-event-schedule__event-header-right">
            <HeartIcon
              isSaved={isSaved}
              onClick={saveIconClickHandler}
              status={status}
            />

            {renderEventStatusText()}
          </div>
        </div>

        {status !== 'ended' && (descriptionWithLink || listingImage || locationMappedinId) && (
          <div className="c-event-schedule__event-body">
            <div className="c-event-schedule__event-body-text">
              {descriptionWithLink && !listingImage && parse(descriptionWithLink)}
              {!descriptionWithLink && listingImage && renderListingImage()}

              {descriptionWithLink && listingImage && (
                <div className="c-event-schedule__event-body-text-and-image-container">
                  {parse(descriptionWithLink)}
                  {renderListingImage()}
                </div>
              )}

              {locationMappedinId && (
                <a
                  className="c-event-schedule__link c-event-schedule__event-location"
                  href={locationMappedinId}
                >
                  {locateOnMapDescription || 'Locate on map'}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

Event.propTypes = {
  currentDateTime: PropTypes.instanceOf(DateTime).isRequired,
  currentTimezone: PropTypes.string.isRequired,
  ...eventPropType.isRequired,
};

export default Event;
